/**
 * Čista logika Recepata (bez Vue-a i DOM-a, da se isti kod može provjeriti u Node-u).
 * Računanje je isto kao na staroj stranici: količina namirnice za jednu smjenu je norma po porciji × broj porcija,
 * zbir jela je zbir po smjenama, a zbirna lista skuplja namirnice po nazivu i jedinici. Jedina razlika je
 * zaokruživanje komada: komadi idu naviše, a zbir reda je zbir prikazanih smjena (inače Hljeb 124 + 37 + 1 daje 161).
 */

const PIECE = /^\s*kom\s*$/i;
const EPS = 1e-9;

export const num = (value) => {
  const n = Number(value);
  return value === "" || value == null || Number.isNaN(n) ? 0 : n;
};

export const isPiece = (measure) => PIECE.test(String(measure == null ? "" : measure));

/** Porcije su cijeli broj, najmanje 0 (prazno i nevažeće je 0). */
export const count = (value) => Math.max(0, Math.round(num(value)));

/** Količina za prikaz: komadi naviše (dio komada se ne može pripremiti), ostalo na tri decimale. */
export const roundQty = (value, measure) => {
  const v = num(value);
  if (isPiece(measure)) {
    return Math.max(0, Math.ceil(v - EPS));
  }
  return Math.round(v * 1000 + 1e-7) / 1000;
};

const sum = (list) => list.reduce((total, value) => total + value, 0);

export const editKey = (meal, shift) => `${meal}|${shift}`;

/** Norma po porciji može imati i pet decimala (0,0001 kg bibera), pa se ne zaokružuje na tri. */
export const formatNorm = (value, locale) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 5 }).format(num(value));

/** Današnji datum po lokalnom vremenu (toISOString daje UTC: poslije ponoći bi se otvorio jučerašnji). */
export const localToday = (now = new Date()) => {
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// ------------------------------------------------------------------------ podaci sa servera
/**
 * Odgovor izvještaja → jela sa jedinstvenim nazivom, cijelim smjenama i namirnicama sa brojevima.
 * Dva jela istog naziva postaju "Naziv" i "Naziv (2)", da se izmjene porcija i izbor jela ne slijepe u jedno.
 * @param {Array<{ food_name: string, food_id?: number, shifts?: Array, groceries?: Array }>} rows
 * @returns {Array<{ name: string, foodId: number|null, shifts: Array<{shift:number,quantity:number}>, groceries: Array }>}
 */
export const normalizeReport = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const seen = {};
  return list
    .filter((row) => row && String(row.food_name != null ? row.food_name : row.name || "").trim() !== "")
    .map((row) => {
      const title = String(row.food_name != null ? row.food_name : row.name).trim();
      seen[title] = (seen[title] || 0) + 1;
      const byShift = {};
      (Array.isArray(row.shifts) ? row.shifts : []).forEach((entry) => {
        const shift = Number(entry && entry.shift);
        if (Number.isInteger(shift) && shift > 0) {
          byShift[shift] = (byShift[shift] || 0) + num(entry.quantity);
        }
      });
      return {
        name: seen[title] === 1 ? title : `${title} (${seen[title]})`,
        foodId: row.food_id != null ? row.food_id : null,
        shifts: Object.keys(byShift)
          .map(Number)
          .sort((a, b) => a - b)
          .map((shift) => ({ shift, quantity: byShift[shift] })),
        groceries: (Array.isArray(row.groceries) ? row.groceries : [])
          .filter((g) => g && String(g.name == null ? "" : g.name).trim() !== "")
          .map((g) => ({
            id: g.id,
            name: String(g.name).trim(),
            measure: g.measure == null ? "" : String(g.measure).trim(),
            quantity: num(g.quantity),
            price: g.price == null || g.price === "" ? null : num(g.price),
          })),
      };
    });
};

// ------------------------------------------------------------------------ računanje
/**
 * Izračun jednog jela za izabrane smjene.
 * meal: { name, shifts: [{ shift, quantity }], groceries: [{ id, name, measure, quantity, price }] }
 * options: { shifts: [1, 2, 3], edits: { "naziv|smjena": porcije } }
 * Svaka izabrana smjena je kolona (i kad jelo u njoj nema porcija), da su kartice poravnate i da se porcije
 * mogu upisati i za smjenu u kojoj nema narudžbi.
 */
export const mealCalc = (meal, options) => {
  const shifts = options.shifts.slice().sort((a, b) => a - b);
  const edits = options.edits || {};
  const base = {};
  (meal.shifts || []).forEach((s) => {
    base[s.shift] = count(s.quantity);
  });
  const cols = shifts.map((shift) => {
    const ordered = base[shift] || 0;
    const key = editKey(meal.name, shift);
    const has = Object.prototype.hasOwnProperty.call(edits, key);
    const planned = has ? count(edits[key]) : ordered;
    return { shift, base: ordered, count: planned, edited: has && planned !== ordered, delta: planned - ordered };
  });
  const portions = sum(cols.map((c) => c.count));
  const rows = (meal.groceries || []).map((g) => {
    const piece = isPiece(g.measure);
    const exact = cols.map((c) => num(g.quantity) * c.count);
    const shown = exact.map((v) => roundQty(v, g.measure));
    const exactTotal = sum(exact);
    const total = piece ? sum(shown) : roundQty(exactTotal, g.measure);
    const price = g.price == null ? null : num(g.price);
    return {
      id: g.id,
      name: g.name,
      measure: g.measure || "",
      norm: num(g.quantity),
      price,
      exact,
      shown,
      exactTotal,
      total,
      cost: price == null ? null : price * (piece ? total : exactTotal),
    };
  });
  const cost = sum(rows.map((r) => r.cost || 0));
  return {
    name: meal.name,
    foodId: meal.foodId != null ? meal.foodId : null,
    cols,
    portions,
    rows,
    hasRecipe: rows.length > 0,
    cost,
    perPortion: portions ? cost / portions : 0,
    edited: cols.filter((c) => c.edited).length,
  };
};

/** Zbirna lista: namirnice svih jela, skupljene po nazivu i jedinici (kao stara kartica "Ukupno"). */
export const totalsCalc = (calcs, shifts) => {
  const list = shifts.slice().sort((a, b) => a - b);
  const map = {};
  const order = [];
  calcs.forEach((m) => {
    m.rows.forEach((r) => {
      const key = `${r.name}_${r.measure}`;
      if (!map[key]) {
        map[key] = { key, name: r.name, measure: r.measure, price: r.price, exact: list.map(() => 0), parts: [] };
        order.push(key);
      }
      const t = map[key];
      r.exact.forEach((v, i) => {
        t.exact[i] += v;
      });
      if (t.price == null && r.price != null) {
        t.price = r.price;
      }
      t.parts.push({
        meal: m.name,
        norm: r.norm,
        counts: m.cols.map((c) => c.count),
        exact: r.exact,
        exactTotal: r.exactTotal,
      });
    });
  });
  return order.map((key) => {
    const t = map[key];
    const piece = isPiece(t.measure);
    const shown = t.exact.map((v) => roundQty(v, t.measure));
    const exactTotal = sum(t.exact);
    const total = piece ? sum(shown) : roundQty(exactTotal, t.measure);
    const parts = t.parts.filter((p) => p.exactTotal > 0).sort((a, b) => b.exactTotal - a.exactTotal);
    return {
      key: t.key,
      name: t.name,
      measure: t.measure,
      price: t.price,
      exact: t.exact,
      shown,
      exactTotal,
      total,
      cost: t.price == null ? null : t.price * (piece ? total : exactTotal),
      meals: parts.length,
      parts,
    };
  });
};

const UNIT_RANK = { kg: 1, g: 2, l: 3, ml: 4, kom: 5 };
const unitRank = (measure) => UNIT_RANK[String(measure == null ? "" : measure).trim().toLowerCase()] || 9;

/**
 * Redoslijed zbirne liste: po nazivu, smjeni (ključ "s1".."s4"), ukupnoj količini, trošku ili broju jela.
 * Količine se ne porede preko različitih jedinica (kilogrami nisu veći od komada): prvo kg, pa l, pa kom,
 * a smjer važi unutar jedinice.
 */
export const sortTotals = (rows, key, dir) => {
  const d = dir < 0 ? -1 : 1;
  const byQty = key === "total" || /^s\d$/.test(key);
  const get = (r) => {
    if (key === "name") return r.name;
    if (key === "total") return r.total;
    if (key === "cost") return r.cost == null ? -1 : r.cost;
    if (key === "meals") return r.meals;
    const m = /^s(\d)$/.exec(key);
    return m ? r.shown[Number(m[1]) - 1] || 0 : 0;
  };
  return rows.slice().sort((a, b) => {
    if (byQty && unitRank(a.measure) !== unitRank(b.measure)) {
      return unitRank(a.measure) - unitRank(b.measure);
    }
    const x = get(a);
    const y = get(b);
    const c = typeof x === "string" ? x.localeCompare(y, "sr") : x - y;
    return c * d || a.name.localeCompare(b.name, "sr");
  });
};

// ------------------------------------------------------------------------ pretraga
const CYR = {
  а: "a", б: "b", в: "v", г: "g", д: "d", ђ: "đ", е: "e", ж: "ž", з: "z", и: "i", ј: "j", к: "k", л: "l", љ: "lj",
  м: "m", н: "n", њ: "nj", о: "o", п: "p", р: "r", с: "s", т: "t", ћ: "ć", у: "u", ф: "f", х: "h", ц: "c", ч: "č",
  џ: "dž", ш: "š",
};

/** Slova bez razlike između ćirilice i latinice, velikih i malih slova i kvačica (č, ć, š, ž, đ); razmaci ostaju. */
export const fold = (text) =>
  String(text == null ? "" : text)
    .toLowerCase()
    .replace(/[Ѐ-ӿ]/g, (c) => CYR[c] || c)
    .replace(/đ/g, "dj")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export const norm = (text) => fold(text).replace(/\s+/g, " ").trim();

/** Gdje se upit nalazi u tekstu: [od, do) u izvornom tekstu (za isticanje), ili null. */
export const findAt = (text, query) => {
  const q = norm(query);
  if (!q) {
    return null;
  }
  const source = String(text == null ? "" : text);
  const map = [];
  let folded = "";
  for (let i = 0; i < source.length; i++) {
    const piece = fold(source.charAt(i));
    for (let j = 0; j < piece.length; j++) {
      map.push(i);
    }
    folded += piece;
  }
  const at = folded.indexOf(q);
  return at < 0 ? null : [map[at], map[at + q.length - 1] + 1];
};

export const matches = (name, query) => {
  const q = norm(query);
  return !q || norm(name).indexOf(q) !== -1;
};

/** Tekst podijeljen oko pogotka, za isticanje: { before, hit, after }. */
export const splitHit = (text, query) => {
  const source = String(text == null ? "" : text);
  const at = query ? findAt(source, query) : null;
  if (!at) {
    return { before: source, hit: "", after: "" };
  }
  return { before: source.slice(0, at[0]), hit: source.slice(at[0], at[1]), after: source.slice(at[1]) };
};

/**
 * Kartice jela za pretragu: pogodak u nazivu jela zadržava sve namirnice, pogodak u namirnici samo te namirnice.
 * @returns {Array<{ c: object, rows: Array, q: string, mealHit?: boolean }>}
 */
export const filterCards = (calcs, query, nameOf = (x) => x) => {
  const q = String(query || "").trim();
  return calcs
    .map((c) => {
      if (!q) {
        return { c, rows: c.rows, q: "" };
      }
      const mealHit = matches(nameOf(c.name), q);
      const rows = mealHit ? c.rows : c.rows.filter((r) => matches(nameOf(r.name), q));
      return rows.length || mealHit ? { c, rows, q, mealHit } : null;
    })
    .filter(Boolean);
};

/** Redoslijed kartica: "name" po nazivu, "portions" po broju porcija (od najvećeg). */
export const sortCardEntries = (entries, order) =>
  entries.slice().sort((a, b) =>
    order === "portions"
      ? b.c.portions - a.c.portions || a.c.name.localeCompare(b.c.name, "sr")
      : a.c.name.localeCompare(b.c.name, "sr")
  );

// ------------------------------------------------------------------------ izmjene porcija
/** Izmjene porcija: vraća novi objekat; vrijednost jednaka naručenoj briše izmjenu. */
export const applyEdit = (edits, meal, shift, value, baseCount) => {
  const next = {};
  const key = editKey(meal, shift);
  const planned = count(value);
  Object.keys(edits).forEach((k) => {
    if (k !== key) {
      next[k] = edits[k];
    }
  });
  if (planned !== baseCount) {
    next[key] = planned;
  }
  return next;
};

/** Izmjene bez onih koje se odnose na jedno jelo. */
export const withoutMeal = (edits, meal) => {
  const next = {};
  Object.keys(edits).forEach((k) => {
    if (k.indexOf(`${meal}|`) !== 0) {
      next[k] = edits[k];
    }
  });
  return next;
};

export const editCount = (edits) => Object.keys(edits).length;

// ------------------------------------------------------------------------ izvoz
/** Nazivi listova za Excel: bez * ? : \ / [ ], najviše 31 znak, jedinstveni bez obzira na veličinu slova. */
export const sheetNames = (titles) => {
  const used = {};
  return titles.map((title) => {
    const base =
      String(title == null || title === "" ? "Sheet" : title)
        .replace(/[*?:\\/[\]]/g, "-")
        .replace(/^'+|'+$/g, "")
        .trim()
        .slice(0, 31) || "Sheet";
    let name = base;
    let n = 2;
    while (used[name.toLowerCase()]) {
      const suffix = ` (${n++})`;
      name = base.slice(0, 31 - suffix.length) + suffix;
    }
    used[name.toLowerCase()] = true;
    return name;
  });
};

/** Tekst za štampu: znakovi koji bi se tumačili kao HTML ne smiju proći (štampa upisuje tekst u HTML). */
export const escapeHtml = (text) =>
  String(text == null ? "" : text).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
